import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Sidebar from './Sidebar';
import { Menu, LayoutDashboard, ShoppingBag, Package, BarChart3, Users, LogOut, Tag, ShieldCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

const BOTTOM_NAV = [
  { to: '/dashboard',  icon: LayoutDashboard, label: 'Home'       },
  { to: '/orders',     icon: ShoppingBag,     label: 'Orders'     },
  { to: '/products',   icon: Package,         label: 'Products'   },
  { to: '/categories', icon: Tag,             label: 'Categories' },
  { to: '/customers',  icon: ShieldCheck,     label: 'Customers'  },
  { to: '/sellers',    icon: Users,           label: 'Sellers'    },
  { to: '/analytics',  icon: BarChart3,       label: 'Analytics'  },
];

export default function Layout({ children, title, subtitle }) {
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [scrolled, setScrolled]       = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onResize = () => { if (window.innerWidth >= 1024) setSidebarOpen(false); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [sidebarOpen]);

  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!showLogoutConfirm) return;
    const onKey = (e) => { if (e.key === 'Escape') setShowLogoutConfirm(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showLogoutConfirm]);

  return (
    <div className="admin-layout">
      {/* Overlay */}
      {sidebarOpen && (
        <button
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
          aria-label="Close sidebar"
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'block', width: '100%', height: '100%' }}
        />
      )}

      {/* Desktop Sidebar */}
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="main-content">
        {/* Topbar */}
        <header className={`topbar${scrolled ? ' scrolled' : ''}`}>
          <button className="hamburger-btn" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
            <Menu size={20} />
          </button>

          <div className="topbar-titles">
            <div className="topbar-title">{title}</div>
            {subtitle && <div className="topbar-subtitle">{subtitle}</div>}
          </div>

          <div className="topbar-right">
            <div className="topbar-user-chip">
              <div className="avatar" style={{ width: 32, height: 32, fontSize: 12 }}>
                {user?.fullName?.[0] || 'A'}
              </div>
              <div className="topbar-user-name" style={{ fontSize: 13, fontWeight: 600 }}>
                {user?.fullName || 'Admin'}
              </div>
            </div>
            <button
              className="topbar-logout-btn"
              onClick={() => setShowLogoutConfirm(true)}
              title="Logout"
              aria-label="Logout"
            >
              <LogOut size={18} />
              <span className="topbar-logout-label">Logout</span>
            </button>
          </div>
        </header>

        <main className="page-content">
          {children}
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <nav className="bottom-nav" aria-label="Mobile navigation">
        {BOTTOM_NAV.map(({ to, icon: Icon, label }) => (
          <NavLink key={to} to={to} className={({ isActive }) => `bnav-item${isActive ? ' bnav-active' : ''}`}>
            {({ isActive }) => (
              <>
                <div className="bnav-pill">
                  <Icon size={20} strokeWidth={isActive ? 2.5 : 1.8} />
                </div>
                <span className="bnav-label">{label}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div
          className="logout-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.45)',
            backdropFilter: 'blur(2px)',
            WebkitBackdropFilter: 'blur(2px)',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowLogoutConfirm(false); }}
        >
          <dialog
            open
            className="logout-modal"
            aria-modal="true"
            aria-label="Logout confirmation"
            style={{ position: 'static', margin: 0, border: 'none' }}
          >
            <div className="logout-modal-icon">
              <LogOut size={28} />
            </div>
            <h3 className="logout-modal-title">Logout?</h3>
            <p className="logout-modal-text">Are you sure you want to logout from the admin panel?</p>
            <div className="logout-modal-btns">
              <button className="logout-cancel-btn" onClick={() => setShowLogoutConfirm(false)}>
                Cancel
              </button>
              <button className="logout-confirm-btn" onClick={handleLogout}>
                Logout
              </button>
            </div>
          </dialog>
        </div>
      )}
    </div>
  );
}

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
};

Layout.defaultProps = {
  title: '',
  subtitle: '',
};